import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "../context/AuthContext.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

const css = `
:root{
  --prc-accent:#6E5BFF;
  --prc-accent-2:#A78BFA;
  --prc-ink:#111318;
  --prc-text:#0b0b0c;
  --prc-muted:#6b7280;
  --prc-bg: radial-gradient(1200px 600px at 50% -10%, #f4f6ff, #f7f9fc 40%, #f7f9fc);
  --prc-card: rgba(255,255,255,.72);
  --prc-card-solid:#fff;
  --prc-border: rgba(17, 24, 39, .08);
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-black:#0b0b0c;
}

/* Scroll area under sticky navbar */
.page-scroll { height: calc(100vh - var(--nav-h)); overflow: auto; }
@media (max-width: 640px) { .page-scroll { height: calc(100svh - var(--nav-h)); } }
.page-scroll.page-scroll-locked {
  height: 100%;
}
@media (max-width: 640px) {
  .page-scroll.page-scroll-locked {
    height: 100%;
  }
}

/* Custom scrollbar */
.page-scroll { scrollbar-width: thin; scrollbar-color: #a78bfa #eef1ff; } /* Firefox */
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track { background:#eef1ff; border-left:1px solid var(--nav-border); }
.page-scroll::-webkit-scrollbar-thumb { background:linear-gradient(180deg,#c7d2fe,#a78bfa); border:2px solid #eef1ff; border-radius:8px; }

/* Outer pricing background */
.prc-shell{
  min-height:100%;
  background: var(--prc-bg);
  padding: 28px 0 80px;
  color: var(--prc-text);
}

/* Inner container (matches Home-style width/padding) */
.prc-inner{
  max-width:960px;
  margin:0 auto;
  padding:0 40px;
  box-sizing:border-box;
}

@media (max-width: 560px){
  .prc-inner{
    padding:0 18px;
  }
}

/* Header */
.prc-header{
  max-width: 900px;
  margin: 0 auto 20px;
  text-align:center;
}

.prc-eyebrow{
  display:inline-block;
  font-size:12px;
  letter-spacing:.14em;
  text-transform:uppercase;
  color:#7c8195;
  background:#eef1ff;
  border:1px solid #e5e9ff;
  padding:6px 10px;
  border-radius:999px;
}

.prc-title{
  margin:10px 0 6px;
  font-size: clamp(1.8rem, 2.6vw, 2.6rem);
  color:var(--prc-ink);
}

.prc-sub{
  color:#687086;
  max-width:650px;
  margin:0 auto;
}

/* Billing toggle */
.prc-billing-toggle{
  margin:18px auto 0;
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  border:1px solid var(--prc-border);
  background:#f3f4ff;
  gap:4px;
}

.prc-billing-toggle-btn{
  border:none;
  background:transparent;
  font-size:13px;
  padding:6px 12px;
  border-radius:999px;
  cursor:pointer;
  font-weight:600;
  color:#6b7280;
}

.prc-billing-toggle-btn.active{
  background:#fff;
  box-shadow:0 8px 18px rgba(15,23,42,.16);
  color:#111827;
}

.prc-billing-note{
  margin-top:8px;
  font-size:12px;
  color:#6b7280;
}

/* Grid */
.prc-grid{
  max-width:900px;
  margin: 26px auto 0;
  display:grid;
  gap:16px;
  grid-template-columns: repeat(12, 1fr);
}

/* Cards */
.prc-card{
  grid-column: span 6;
  background: var(--prc-card);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border:1px solid var(--prc-border);
  border-radius:16px;
  padding:18px;
  box-shadow: 0 16px 40px rgba(16,24,40,.08);
  display:flex;
  flex-direction:column;
  gap:14px;
  transition: transform .18s ease, box-shadow .18s ease;
}

.prc-card:hover{
  transform: translateY(-2px);
  box-shadow: 0 22px 60px rgba(16,24,40,.12);
}

.prc-card.popular{
  background: linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.85));
  border:1px solid #e8e4ff;
  box-shadow: 0 28px 80px rgba(110,91,255,.18);
}

/* “Most popular” pill */
.prc-popular-pill{
  align-self:flex-start;
  font-size:11px;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:.14em;
  color:#4c1d95;
  background:#ede9fe;
  border-radius:999px;
  padding:4px 10px;
}

/* Plan text */
.prc-name{
  font-weight:800;
  color:var(--prc-ink);
  font-size:1.15rem;
}

.prc-short{
  font-size:12px;
  text-transform:uppercase;
  letter-spacing:.16em;
  color:#9ca3af;
  font-weight:600;
}

.prc-desc{
  color:#6f768a;
  min-height:34px;
}

/* Price & trial */
.prc-price{
  display:flex;
  align-items:baseline;
  gap:6px;
  color:var(--prc-ink);
}

.prc-price .amt{
  font-size:2rem;
  font-weight:900;
}

.prc-price .per{
  color:#8a90a5;
  font-weight:600;
}

.prc-trial{
  font-size:12px;
  color:#6f768a;
}

.prc-card.popular .prc-trial{
  color:#4b5563;
  font-weight:600;
}

/* Includes pill */
.prc-credits{
  font-size:13px;
  color:#3b4152;
  background:#f3f5ff;
  border:1px solid #e4e7ff;
  padding:6px 10px;
  border-radius:999px;
  align-self:flex-start;
}

/* CTA */
.prc-cta{
  display:flex;
  gap:10px;
  align-items:center;
}

.prc-btn{
  flex:1 1 auto;
  border:1px solid var(--prc-border);
  background:#fff;
  color:#0e1020;
  padding:12px 14px;
  border-radius:12px;
  font-weight:800;
  cursor:pointer;
  transition: transform .14s ease, box-shadow .18s ease, background .18s ease;
}

.prc-btn:hover{
  transform: translateY(-1px);
  background:#f7f9ff;
  box-shadow:0 10px 28px rgba(10,20,60,.08);
}

.prc-btn.primary{
  background: linear-gradient(90deg, var(--prc-accent), var(--prc-accent-2));
  color:#fff;
  border-color: transparent;
  box-shadow: 0 16px 40px rgba(110,91,255,.28);
}

.prc-btn.primary:hover{
  filter: brightness(.98);
}

.prc-btn.disabled{
  opacity: 0.5;
  cursor: not-allowed;
  background: #f3f4ff !important;
  color: #6b7280 !important;
}

/* Features list */
.prc-ul{
  list-style:none;
  margin:0;
  padding:0;
  display:grid;
  gap:8px;
}

.prc-li{
  display:flex;
  gap:10px;
  align-items:flex-start;
  color:#333a4f;
  font-size:13px;
}

.prc-li i{
  width:18px;
  height:18px;
  border-radius:50%;
  margin-top:2px;
  background: linear-gradient(90deg, var(--prc-accent), var(--prc-accent-2));
  box-shadow: 0 6px 14px rgba(110,91,255,.25);
}

/* FAQ */
.prc-faq{
  max-width: 900px;
  margin: 34px auto 0;
  display:grid;
  gap:10px;
}

.prc-qa{
  background:#fff;
  border:1px solid var(--prc-border);
  border-radius:12px;
  padding:14px 16px;
}

.prc-q{
  font-weight:800;
  color:#0f1320;
}

.prc-a{
  margin-top:6px;
  color:#5d667c;
  font-size:14px;
}

/* Footer – same styles as Home */
.ps-footer{
  border-top:1px solid rgba(148,163,184,0.35);
  padding-top:18px;
  margin-top:10px;
}
.ps-footer-inner{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  flex-wrap:wrap;
}
.ps-footer-brand{
  display:flex;
  align-items:center;
  gap:10px;
}
.ps-footer-logo{
  width:26px; height:26px; border-radius:9px;
  display:inline-flex; align-items:center; justify-content:center;
  color:#fff; font-size:16px; font-weight:800;
}
.ps-footer-title{
  font-weight:700; font-size:15px; color:var(--ps-black);
}
.ps-footer-brand p{
  margin:0; font-size:13px; color:#9ca3af;
}
.ps-footer-links{
  display:flex; flex-wrap:wrap; gap:10px;
  font-size:13px;
}
.ps-footer-links a{
  text-decoration:none;
  color:#6b7280;
}
.ps-footer-links a:hover{
  color:#000;
  text-decoration:underline;
}
.ps-footer-bottom{
  margin-top:8px;
  font-size:12px;
  color:#9ca3af;
}

/* Responsive */
@media (max-width: 720px){
  .prc-card{ grid-column: 1 / -1; }
}

.prc-escape-row{
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.prc-escape-btn{
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: #6b7280;
  text-decoration: underline;
  cursor: pointer;
}

.prc-escape-btn:hover{
  color: #111827;
}
`;

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const PLANS = [
  {
    key: "free",
    name: "Free",
    desc: "Try MusicBase with 3 searches per month.",
    monthly: 0,
    annual: 0,
    highlight: false,
    features: [
      "Unlimited playlist searches",
      "3 searches per month",
      "Email campaigns & follow-ups",
      "Full database access",
      "Export functionality",
    ],
    priceId: null,
    mode: "free",
    isFree: true,
  },
  {
    key: "outreach",
    name: "Backstage Pass",
    desc: "Unlimited reveals + campaigns for full outreach.",
    monthly: 15,
    annual: 100,
    highlight: true,
    features: [
      "Everything in Free",
      "Unlimited contact reveals",
      "Priority support",
      "Advanced campaign features",
    ],
    priceId: {
      monthly: import.meta.env.VITE_STRIPE_PRICE_OUTREACH,
      annual: import.meta.env.VITE_STRIPE_PRICE_OUTREACH_ANNUAL,
    },
    mode: "subscription",
  },
];

export default function Pricing() {
  const seo = seoPresets["/pricing"];
  const { user, logout, subscription } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const params = new URLSearchParams(loc.search);
  const locked = params.get("locked") === "1";
  const presetPlanKey = params.get("plan");       // "basic" | "outreach" or null
  const presetBilling = params.get("billing");    // "monthly" | "annual" or null
  const shouldAutoStart = params.get("start") === "1";

  const [loadingKey, setLoadingKey] = useState("");
  
  // CHANGED: Default to 'annual' unless 'monthly' is explicitly requested
  const [billingPeriod, setBillingPeriod] = useState(
    presetBilling === "monthly" ? "monthly" : "annual"
  );
  
  const [autoStarted, setAutoStarted] = useState(false);

  const back = encodeURIComponent(loc.pathname + (loc.search || ""));

  async function handleLogout() {
    try {
      await logout();
      nav("/pricing");
    } catch (e) {
      console.error(e);
      alert("Could not log out. Please try again.");
    }
  }

  async function startCheckout(plan) {
    const periodKey = billingPeriod === "annual" ? "annual" : "monthly";
    if (plan.isFree) {
      if (!user) {
        nav(`/signup?redirect=${encodeURIComponent('/pricing')}`);
      } else {
        if (locked) {
          nav("/search");
        } else {
          nav("/pricing");
        }
      }
      return;
    }

    const priceId =
      typeof plan.priceId === "string"
        ? plan.priceId
        : plan.priceId?.[periodKey];

    if (!priceId) {
      alert("No price configured for this billing period yet.");
      return;
    }

    if (!user) {
      const redirectTarget = `/pricing?locked=1&plan=${plan.key}&billing=${periodKey}&start=1`;
      const redirectEncoded = encodeURIComponent(redirectTarget);
      nav(`/signup?redirect=${redirectEncoded}&need=checkout`);
      return;
    }

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("start") === "1") {
        url.searchParams.delete("start");
        const newSearch = url.searchParams.toString();
        nav(`${url.pathname}${newSearch ? "?" + newSearch : ""}`, {
          replace: true,
        });
      }
    } catch (e) {
      console.warn("Failed to clean start param", e);
    }

    try {
      if (window.fbq) {
        const isAnnual = periodKey === "annual";
        const value = isAnnual ? plan.annual : plan.monthly;

        window.fbq("track", "InitiateCheckout", {
          value,
          currency: "USD",
          plan: plan.name,
          billing_period: periodKey,
        });
      }
    } catch (e) {
      console.warn("Meta Pixel InitiateCheckout error", e);
    }

    setLoadingKey(plan.key);
    try {
      const res = await fetch(
        "https://api.musicbase.io/api/stripe/create-checkout-session",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            priceId,
            mode: plan.mode,
            billingPeriod: periodKey,
          }),
        }
      );

      if (res.status === 401) {
        nav(`/signup?redirect=${back}&need=checkout`);
        return;
      }
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Checkout failed (${res.status}). ${t}`);
      }

      const data = await res.json();

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe.js failed to load");
      await stripe.redirectToCheckout({ sessionId: data.id });
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not start checkout.");
    } finally {
      setLoadingKey("");
    }
  }

  useEffect(() => {
    if (!user) return;
    if (!shouldAutoStart) return;
    if (autoStarted) return;

    const plan = PLANS.find((p) => p.key === presetPlanKey);
    if (!plan) return;

    setAutoStarted(true);
    startCheckout(plan);
  }, [user, shouldAutoStart, autoStarted, presetPlanKey]);

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
      />
      <style>{css}</style>
      <div className={locked ? "page-scroll page-scroll-locked" : "page-scroll"}>
        <div className="prc-shell">
          <div className="prc-inner">
            <header className="prc-header">
              <span className="prc-eyebrow">Choose Your Plan</span>
              <h1 className="prc-title">Start free, upgrade when ready</h1>
              <p className="prc-sub">
                Start with <strong>Free</strong> to try 3 searches per month, or upgrade to <strong>Backstage Pass</strong> for unlimited access.
              </p>

              {/* Billing toggle */}
              <div
                className="prc-billing-toggle"
                role="tablist"
                aria-label="Billing period"
              >
                <button
                  type="button"
                  className={
                    "prc-billing-toggle-btn" +
                    (billingPeriod === "monthly" ? " active" : "")
                  }
                  onClick={() => setBillingPeriod("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={
                    "prc-billing-toggle-btn" +
                    (billingPeriod === "annual" ? " active" : "")
                  }
                  onClick={() => setBillingPeriod("annual")}
                >
                  Annual
                </button>
              </div>
              <p className="prc-billing-note">
                {billingPeriod === "annual"
                  ? "Pay once per year. Cancel any time."
                  : "Billed monthly. Cancel any time."}
              </p>

              {locked && user && (
                <div className="prc-escape-row">
                  <button
                    type="button"
                    className="prc-escape-btn"
                    onClick={handleLogout}
                  >
                    Not this account? Log out
                  </button>
                </div>
              )}
            </header>

            <section className="prc-grid">
              {PLANS.map((p) => {
                const isAnnual = billingPeriod === "annual";
                const isFree = p.isFree;

                const periodKey = isAnnual ? "annual" : "monthly";
                const currentPriceId =
                  !isFree &&
                  (typeof p.priceId === "string"
                    ? p.priceId
                    : p.priceId?.[periodKey]);

                const hasActivePaidSubscription =
                  !!subscription &&
                  subscription.status === "active" &&
                  !!subscription.price_id;

                const isCurrentFreePlan =
                  !locked && user && isFree && !hasActivePaidSubscription;

                const isCurrentPaidPlan =
                  user &&
                  !isFree &&
                  subscription?.status === "active" &&
                  subscription.price_id === currentPriceId;

                const isCurrent = isCurrentFreePlan || isCurrentPaidPlan;

                // CHANGED: Logic for displaying price to show $8.33/mo on annual
                let priceLabel = "";
                let perLabel = "";
                let billingText = "";

                if (isFree) {
                  priceLabel = "Free";
                  perLabel = "";
                  billingText = "No credit card required";
                } else {
                  // For Annual paid plans, show monthly equivalent
                  if (isAnnual) {
                    const monthlyEquivalent = (p.annual / 12).toFixed(2);
                    priceLabel = `$${monthlyEquivalent}`;
                    perLabel = "/month";
                    billingText = `Billed annually at $${p.annual} per year.`;
                  } else {
                    // Monthly paid plans
                    priceLabel = `$${p.monthly}`;
                    perLabel = "/month";
                    billingText = "Billed monthly · Cancel anytime";
                  }
                }

                return (
                  <article
                    key={p.key}
                    className={
                      "prc-card" +
                      (p.highlight ? " popular" : "") +
                      (isCurrent && isFree ? " disabled" : "")
                    }
                  >
                    {p.popularLabel && (
                      <div className="prc-popular-pill">{p.popularLabel}</div>
                    )}

                    <div className="prc-name">{p.name}</div>
                    {p.short && <div className="prc-short">{p.short}</div>}
                    <div className="prc-desc">{p.desc}</div>

                    <div className="prc-price">
                      <span className="amt">{priceLabel}</span>
                      <span className="per">{perLabel}</span>
                    </div>

                    <div className="prc-trial">{billingText}</div>

                    {p.includes && (
                      <span className="prc-credits">{p.includes}</span>
                    )}

                    <div className="prc-cta">
                      <button
                        className={
                          "prc-btn" + (p.highlight ? " primary" : "") + (isCurrent ? " disabled" : "")
                        }
                        onClick={() => startCheckout(p)}
                        disabled={loadingKey === p.key || isCurrent}
                      >
                        {loadingKey === p.key ? "Starting…" : (isCurrent ? "Subscribed" : (isFree ? "Get Started" : "Subscribe"))}
                      </button>
                    </div>

                    <ul className="prc-ul">
                      {p.features.map((f, i) => (
                        <li key={i} className="prc-li">
                          <i aria-hidden="true"></i>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </section>

            <section className="prc-faq">
              <div className="prc-qa">
                <div className="prc-q">What's included in Backstage Pass?</div>
                <div className="prc-a">
                  <strong>Backstage Pass</strong> gives you unlimited playlist searches, unlimited contact reveals, email campaigns with reply tracking, and priority support. Everything you need for serious playlist outreach.
                </div>
              </div>

              <div className="prc-qa">
                <div className="prc-q">What about the free version?</div>
                <div className="prc-a">
                  All users start with unlimited searches and <strong>3 searches per month</strong> automatically. You also get full access to campaigns and email features. No credit card required.
                </div>
              </div>

              <div className="prc-qa">
                <div className="prc-q">Can I cancel anytime?</div>
                <div className="prc-a">
                  Yes! You can cancel your Backstage Pass subscription at any time. Cancellations take effect at the next renewal, and you'll keep access until the end of your current billing period.
                </div>
              </div>
            </section>

            {!locked && (
              <footer className="ps-footer">
                <div className="ps-footer-inner">
                  <div className="ps-footer-brand">
                    <span className="ps-footer-logo" aria-hidden>
                      <img
                        className="brand-icon"
                        src="https://www.musicbase.io/logo.png"
                        alt=""
                        aria-hidden
                        style={{ height: "25px", width: "25px" }}
                      />
                    </span>
                    <div>
                      <div className="ps-footer-title">MusicBase</div>
                      <p>Playlist outreach built for artists, not sales teams.</p>
                    </div>
                  </div>

                  <nav className="ps-footer-links" aria-label="Footer">
                    <a href="/">Home</a>
                    <a href="/pricing">Pricing</a>
                    <a href="/login">Log in</a>
                    <a href="/signup">Sign up</a>
                    <a href="/contact">Contact</a>
                    <a href="/terms">Terms</a>
                    <a href="/privacy">Privacy Policy</a>
                  </nav>
                </div>
                <div className="ps-footer-bottom">
                  © {new Date().getFullYear()} MusicBase. All rights reserved.
                </div>
              </footer>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
